package com.zi.support.web

import com.zi.support.shared.RecentSearch

import com.zi.support.shared.NotificationKinds
import com.zi.support.shared.EmptyState
import com.zi.support.shared.EmptyStates
import com.zi.support.shared.IncidentFields
import com.zi.support.shared.IncidentFilters
import com.zi.support.shared.IncidentSection
import com.zi.support.shared.IncidentSeverity
import kotlinx.browser.document
import org.w3c.dom.HTMLInputElement

internal object IssuesScreen {
    private var rows: List<WebRecord> = emptyList()
    /** The newest 200 incidents (any status), and every open one however old: an open issue past the newest 200 still lists. */
    private var recent: List<WebRecord> = emptyList()
    private var openOnes: List<WebRecord> = emptyList()
    private var stop: dynamic = null
    private var stopOpen: dynamic = null
    private var selecting = false
    private var loaded = false
    private var resolvedTotal: Int? = null
    private var resolvedCountFailed = false
    private var countRequest = 0
    private val selectedIds = linkedSetOf<String>()
    private var appFilter = ""
    private var companyFilter = ""
    private var severityFilter: Int? = null
    /** companyId to name, from the `companies` collection. */
    private var directory: Map<String, String> = emptyMap()
    /** Every incident from the last RecentSearch.DAYS days (any status), loaded once, the first time someone searches or picks a company. */
    private var older: List<WebRecord>? = null
    /** The Insights toggle: an animated infographic of the issues matching the filters, instead of the cards. */
    private var insights = false
    private var insightsAnimate = true
    private val insightRange = Infographic.Range()
    /** Issues reported in the Insights period and the one before it (for the trend); null while loading. */
    private var insightRows: List<WebRecord>? = null
    private var insightKey = ""

    fun render() {
        stop()
        if (MobileInsights.enabled) { companyFilter = MobileInsights.filter("company"); appFilter = MobileInsights.filter("app"); severityFilter = MobileInsights.filter("severity").toIntOrNull() }
        insights = MobileInsights.enabled; rows = emptyList(); recent = emptyList(); openOnes = emptyList(); older = null; loaded = false; resolvedTotal = null; resolvedCountFailed = false; cancelBatchSelection()
        element("content")?.innerHTML = """
          <div class="issue-page"><div class="stats-grid skeleton-stats" id="issue-stats"><div class="stat-card skeleton"></div><div class="stat-card skeleton"></div><div class="stat-card skeleton"></div><div class="stat-card skeleton"></div></div><div class="toolbar filter-panel" id="issue-controls" style="visibility:hidden">
            <div class="segmented"><button data-status="open" class="active">Open</button><button data-status="resolved">Resolved</button><button data-status="all">All</button></div>
            <div class="search-shell"><span class="material-symbols-rounded" aria-hidden="true">search</span><label class="visually-hidden" for="issue-search">Search issues</label><input id="issue-search" class="input search" placeholder="Search app, company, device or error…"></div>
            <div class="filter-chips" id="issue-chips"></div>
            <button id="issue-insights-toggle" class="btn tk-insights-toggle" aria-pressed="false"><span class="material-symbols-rounded" aria-hidden="true">bar_chart</span><span>Insights</span></button>
            <button id="batch-resolve-issues" class="btn">Select issues to resolve</button><button id="resolve-all-issues" class="btn">Resolve all</button><button id="cancel-batch-issues" class="btn btn-ghost" style="display:none">Cancel</button>
          </div><div id="issue-grid" class="grid"><div class="empty">Loading incidents…</div></div></div>
        """.trimIndent()
        MobileInsights.applyInputs()
        document.querySelectorAll("[data-status]").let { nodes -> for (i in 0 until nodes.length) nodes.item(i)?.addEventListener("click", { event ->
            document.querySelectorAll("[data-status]").let { buttons -> for (j in 0 until buttons.length) (buttons.item(j) as? org.w3c.dom.HTMLElement)?.classList?.remove("active") }
            (event.currentTarget as? org.w3c.dom.HTMLElement)?.classList?.add("active"); cancelBatchSelection(); draw()
        }) }
        type("issue-search") { draw() }; bindSearchClear("issue-search") { draw() }
        promise(FirebaseGateway.getCollection("companies"), { snapshot ->
            val docs = snapshot.docs
            directory = (0 until (docs.length as Int)).associate { i ->
                val d = docs[i].data(); val id = field(d, "companyId").ifBlank { docs[i].id as String }
                id to field(d, "companyName").ifBlank { id }
            }
            draw()
        })
        click("batch-resolve-issues") { if(!selecting){selecting=true;draw()}else if(selectedIds.isNotEmpty())confirmBatchResolve() }
        click("cancel-batch-issues") { cancelBatchSelection();draw() }
        click("resolve-all-issues") { confirmResolveAll() }
        click("issue-insights-toggle") {
            insights = !insights; insightsAnimate = true; cancelBatchSelection()
            if (insights) loadOlderForSearch()  // charts cover the newest ~700 issues, not just the live 200
            element("issue-insights-toggle")?.let { b ->
                b.setAttribute("aria-pressed", insights.toString())
                b.innerHTML = if (insights) "<span class=\"material-symbols-rounded\" aria-hidden=\"true\">arrow_back</span><span>Back to issues</span>" else "<span class=\"material-symbols-rounded\" aria-hidden=\"true\">bar_chart</span><span>Insights</span>"
            }
            draw()
        }
        val fs = FirebaseGateway.firestore
        val query = fs.query(FirebaseGateway.collection("incidents"), fs.orderBy("timestamp", "desc"), fs.limit(200))
        stop = fs.onSnapshot(query, { snapshot: dynamic ->
            val docs: dynamic = snapshot.docs
            val count = docs.length as Int
            recent = (0 until count).map { WebRecord(docs[it].id as String, docs[it].ref.path as String, docs[it].data()) }
            rows = shown((recent + openOnes).distinctBy { it.id })
            refreshResolvedTotal()
            loaded = true
            element("issue-controls")?.style?.visibility = "visible"
            element("issue-stats")?.classList?.remove("skeleton-stats")
            draw()
        }, { error: dynamic -> val message = FirebaseGateway.errorMessage(error); loaded=true;element("issue-stats")?.innerHTML="";element("issue-grid")?.innerHTML = emptyState(EmptyState("Couldn't load issues", message), "wifi_off", "retry-issues", "Retry");click("retry-issues"){render()} })
        stopOpen = fs.onSnapshot(fs.query(FirebaseGateway.collection("incidents"), fs.where("status", "==", "open")), { snapshot: dynamic ->
            val docs: dynamic = snapshot.docs
            openOnes = (0 until (docs.length as Int)).map { WebRecord(docs[it].id as String, docs[it].ref.path as String, docs[it].data()) }
            rows = shown((recent + openOnes).distinctBy { it.id })
            draw()
        }, { _: dynamic -> })
    }

    /** Drops systemdefaults 404s from servers other than Gantrans (IncidentFilters.isIgnoredSystemDefaults404). */
    private fun shown(list: List<WebRecord>) = list.filterNot { IncidentFilters.isIgnoredSystemDefaults404(JSON.stringify(it.data)) }

    private fun severityOf(d: dynamic) = IncidentSeverity.level(field(d, "errorType"), field(d, "errorMessage").ifBlank { field(d, "message") }, field(d, "severity"))
    private fun severityPill(level: Int) = "<span class=\"tk-pill sev-pill sev-$level\" title=\"${IncidentSeverity.title(level)}\">S$level</span>"

    fun stop() { cancelListener(stop); stop = null; cancelListener(stopOpen); stopOpen = null; countRequest++ }

    private fun refreshResolvedTotal() {
        val request = ++countRequest
        val fs = FirebaseGateway.firestore
        val query = fs.query(FirebaseGateway.collection("incidents"), fs.where("status", "==", "resolved"))
        promise(fs.getCountFromServer(query), { result ->
            if (request == countRequest) {
                resolvedTotal = (result.data().count as Number).toInt()
                resolvedCountFailed = false
                draw()
            }
        }, { message ->
            console.warn("Resolved incident count failed: $message")
            if (request == countRequest) {
                resolvedTotal = null
                resolvedCountFailed = true
                draw()
            }
        })
    }

    private fun appOf(d: dynamic) = IncidentFilters.appCategory(field(d, "appName"))
    private fun companyName(d: dynamic) = IncidentFilters.company(field(d, "appName"), field(d, "appVariant"), field(d, "company"))
    private fun companyIdOf(d: dynamic) = companyName(d).takeIf { it.isNotBlank() }?.let(IncidentFilters::companyId).orEmpty()

    /** Every company in the `companies` collection, plus any an incident names that isn't there yet. */
    private fun companyOptions(): Map<String, String> =
        (rows.mapNotNull { r -> companyIdOf(r.data).takeIf { it.isNotBlank() }?.let { it to companyName(r.data) } }.toMap() + directory)
            .entries.sortedBy { it.value.lowercase() }.associate { it.key to it.value }

    /** Loads the issues reported from the start of the period before this one; a period change reloads. */
    private fun loadInsights() {
        val key = "${insightRange.first}-${insightRange.last}"
        if (key == insightKey) return
        insightKey = key; insightRows = null
        val fs = FirebaseGateway.firestore
        // ponytail: capped at 5000 issues; page further if a year outgrows it.
        promise(fs.getDocs(fs.query(FirebaseGateway.collection("incidents"), fs.where("timestamp", ">=", FirebaseGateway.timestampFromMillis(Infographic.midnight(insightRange.first + insightRange.days))),
            fs.orderBy("timestamp", "desc"), fs.limit(5000))),
            { snapshot -> if (key == insightKey) { insightRows = shown(records(snapshot)); if (insights) draw() } },
            { message -> if (key == insightKey) { insightKey = ""; insightRows = emptyList(); toast(message, true); if (insights) draw() } })
    }

    /** Insights for the issues matching the app, company, severity and search filters (open and resolved), reported in the chosen period. */
    private fun drawInsights(root: org.w3c.dom.HTMLElement, all: List<WebRecord>, unfiltered: Boolean) {
        val day = 86_400_000.0
        val start = insightRange.start; val end = insightRange.end
        fun at(d: dynamic) = timestampMillis(d.timestamp)
        fun isResolved(d: dynamic) = field(d, "status") == "resolved"
        val issues = all.filter { at(it.data).let { m -> m >= start && m < end } }
        val resolved = issues.filter { isResolved(it.data) }
        val open = issues.size - resolved.size
        val fixTimes = resolved.mapNotNull { r -> (timestampMillis(r.data.resolvedAt) - at(r.data)).takeIf { timestampMillis(r.data.resolvedAt) > 0 && at(r.data) > 0 && it > 0 } }
        val days = (insightRange.first downTo insightRange.last).map { back ->
            val from = Infographic.midnight(back); val to = Infographic.midnight(back - 1)
            issues.filter { at(it.data).let { m -> m >= from && m < to } }.let { that -> that.size to that.count { isResolved(it.data) } }
        }
        fun ranked(key: (dynamic) -> String) = issues.map { key(it.data).trim() }.filter { it.isNotBlank() }.groupingBy { it }.eachCount()
            .entries.sortedByDescending { it.value }.take(6).map { it.key to it.value }
        val (delta, trend) = Infographic.trend(issues.size, all.count { at(it.data).let { m -> m >= start - insightRange.days * day && m < start } }, insightRange.noun())
        val now = kotlin.js.Date.now()
        val body = Infographic.figures(listOf(
                Infographic.Figure("Reported", "${issues.size}", delta, if (trend == "up") "down" else if (trend == "down") "up" else "", count = issues.size, spark = days.map { it.first }),
                Infographic.Figure("Still open", "$open", if (open > 0) "Need attention" else "All clear", if (open > 0) "down" else "up", count = open),
                Infographic.Figure("Resolved", "${resolved.size}", "${Infographic.percent(resolved.size, issues.size)}% of reported", count = resolved.size, spark = days.map { it.second }),
                Infographic.Figure("Avg time to resolve", fixTimes.takeIf { it.isNotEmpty() }?.let { Infographic.duration(it.average()) } ?: "–",
                    fixTimes.takeIf { it.isNotEmpty() }?.let { "Median ${Infographic.duration(it.sorted()[it.size / 2])}" } ?: ""),
                Infographic.Figure("Apps affected", "${issues.map { appOf(it.data) }.filter { it.isNotBlank() }.distinct().size}",
                    "${issues.map { companyIdOf(it.data) }.filter { it.isNotBlank() }.distinct().size} companies"))) +
            (if (insightRange.days < 2) "" else Infographic.line("Issues reported", insightRange.label(), insightRange.dayLabels(), listOf(
                Infographic.Series("Reported", "#dc2626", days.map { it.first }),
                Infographic.Series("Since resolved", "#0d9488", days.map { it.second })))) +
            Infographic.donut("Severity", "Issues reported", IncidentSeverity.levels.mapIndexed { n, level ->
                Infographic.Part(IncidentSeverity.title(level), issues.count { severityOf(it.data) == level }, listOf("#dc2626", "#ea580c", "#d97706", "#2563eb", "#64748b").getOrElse(n) { "#64748b" }) }, "issues") +
            Infographic.heatmap("When issues happen", Infographic.weekHours(issues.map { at(it.data) }), "issues", "#dc2626") +
            Infographic.donut("Open vs resolved", "Issues reported", listOf(Infographic.Part("Open", open, "#dc2626"), Infographic.Part("Resolved", resolved.size, "#0d9488")), "issues") +
            Infographic.bars("Time to resolve", "Issues resolved", Infographic.DURATIONS.map { it.first },
                listOf(Infographic.Series("Resolved", "#0d9488", Infographic.durationCounts(fixTimes))), empty = "Nothing resolved yet") +
            Infographic.bars("Open for", "Issues still open, by age", Infographic.DURATIONS.map { it.first },
                listOf(Infographic.Series("Open", "#dc2626", Infographic.durationCounts(issues.filter { !isResolved(it.data) && at(it.data) > 0 }.map { now - at(it.data) }))), empty = "Nothing open") +
            Infographic.table("By app", "Issues per app", ranked { appOf(it) }, issues.size, "No apps yet") +
            Infographic.table("By company", "Issues per company", ranked { companyName(it) }, issues.size, "No companies yet") +
            Infographic.table("Most common errors", "By error type", ranked { field(it, "errorType") }, issues.size, "No errors yet")
        root.innerHTML = "<div class=\"ins-controls\">${Infographic.EXPORT_BUTTON}${Infographic.rangeControls(insightRange)}</div>" +
            Infographic.section("Issue insights", "${insightRange.label()}${if (unfiltered && companyFilter.isBlank()) "" else " · issues matching your filters"} · <strong>${issues.size}</strong> reported", body, insightsAnimate)
        Infographic.bindRange(root, insightRange) { insightsAnimate = true; draw() }
        Infographic.bindExport(root, "issues-insights", insightRange) {
            listOfNotNull(companyOptions()[companyFilter]?.let { "company" to it }, appFilter.takeIf { it.isNotBlank() }?.let { "app" to it }).toMap()
        }
        (root.querySelector(".ins") as? org.w3c.dom.HTMLElement)?.let(Infographic::bind)
        insightsAnimate = false
    }

    /** Search: issues from the last 90 days with every typed word somewhere in them (RecentSearch). */
    private fun matchesSearch(search: String, data: dynamic) = createdMillis(data) >= RecentSearch.sinceMillis(kotlin.js.Date.now()) &&
        RecentSearch.matches(search, listOf("appName","appVariant","company","deviceName","errorType","errorMessage","message","details","operatorName",
            "endpoint","serverUrl","diagnosis","suggestedFix","status").map { field(data,it) } + companyName(data))

    private fun loadOlderForSearch() {
        if (older != null) return
        older = emptyList()
        val fs = FirebaseGateway.firestore
        val since = FirebaseGateway.timestampFromMillis(RecentSearch.sinceMillis(kotlin.js.Date.now()))
        promise(fs.getDocs(fs.query(FirebaseGateway.collection("incidents"), fs.where("timestamp", ">=", since), fs.orderBy("timestamp", "desc"), fs.limit(RecentSearch.LIMIT))), { snapshot ->
            val docs: dynamic = snapshot.docs
            older = shown((0 until (docs.length as Int)).map { WebRecord(docs[it].id as String, docs[it].ref.path as String, docs[it].data()) })
            draw()
        }, { older = null })
    }

    private fun activeStatus() = document.querySelector("[data-status].active")?.getAttribute("data-status") ?: "open"
    private fun inStatus(d: dynamic) = activeStatus().let { it == "all" || field(d, "status").ifBlank { "open" } == it }

    private fun chip(id: String, icon: String, label: String, on: Boolean) =
        "<button type=\"button\" id=\"$id\" class=\"filter-chip${if (on) " on" else ""}\" aria-haspopup=\"dialog\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">$icon</span><span class=\"filter-chip-label\">${escape(label)}</span><span class=\"material-symbols-rounded filter-chip-caret\" aria-hidden=\"true\">expand_more</span></button>"

    private fun drawChips() {
        val chips = element("issue-chips") ?: return
        val any = appFilter.isNotBlank() || companyFilter.isNotBlank() || severityFilter != null
        chips.innerHTML = chip("issue-app-chip", "apps", appFilter.ifBlank { "All apps" }, appFilter.isNotBlank()) +
            chip("issue-company-chip", "business", companyOptions()[companyFilter] ?: "All companies", companyFilter.isNotBlank()) +
            chip("issue-severity-chip", "priority_high", severityFilter?.let { "Severity $it" } ?: "All severities", severityFilter != null) +
            (if (any) "<button type=\"button\" id=\"issue-clear-filters\" class=\"btn btn-ghost filter-clear\">Clear</button>" else "")
        val scoped = rows.filter { inStatus(it.data) }
        click("issue-app-chip") {
            val counts = scoped.groupingBy { appOf(it.data) }.eachCount()
            val names = (IncidentFilters.apps + counts.keys.filter { it.isNotBlank() }.sorted()).distinct()
            pickOption("Filter by app", "apps", "All apps", names.map { Triple(it, it, counts[it] ?: 0) }, appFilter) { appFilter = it; draw() }
        }
        click("issue-company-chip") {
            val counts = scoped.groupingBy { companyIdOf(it.data) }.eachCount()
            pickOption("Filter by company", "business", "All companies", companyOptions().map { (id, name) -> Triple(id, name, counts[id] ?: 0) }, companyFilter) { companyFilter = it; draw() }
        }
        click("issue-severity-chip") {
            val counts = scoped.groupingBy { severityOf(it.data) }.eachCount()
            pickOption("Filter by severity", "priority_high", "All severities",
                IncidentSeverity.levels.map { Triple("$it", IncidentSeverity.title(it), counts[it] ?: 0) }, severityFilter?.toString().orEmpty()) { severityFilter = it.toIntOrNull(); draw() }
        }
        click("issue-clear-filters") { appFilter = ""; companyFilter = ""; severityFilter = null; draw() }
    }

    private fun draw() {
        if (!loaded) return
        drawChips()
        val search = (document.getElementById("issue-search") as? HTMLInputElement)?.value?.trim()?.lowercase().orEmpty()
        val active = activeStatus()
        // A search or a company shows every match, open or resolved, including older issues loaded once for it.
        val searching = search.isNotBlank() || companyFilter.isNotBlank()
        if (searching) loadOlderForSearch()
        // Newest first by when it happened; a search lists open issues before resolved ones.
        val pool = (if (searching) (rows + older.orEmpty()).distinctBy { it.id } else rows)
            .sortedWith(compareBy<WebRecord> { if (searching && field(it.data, "status") == "resolved") 1 else 0 }.thenByDescending { occurredMillis(it.data) })
        val filtered = pool.filter { record ->
            val data = record.data
            (searching || inStatus(data)) && (companyFilter.isBlank() || companyIdOf(data) == companyFilter) && (appFilter.isBlank() || appOf(data) == appFilter) && (severityFilter == null || severityOf(data) == severityFilter) &&
                (search.isBlank() || matchesSearch(search, data))
        }
        val grid = element("issue-grid") ?: return
        val batchButton=element("batch-resolve-issues")
        val cancelButton=element("cancel-batch-issues")
        val canBatch=active=="open"
        batchButton?.style?.display=if(canBatch)"" else "none"
        cancelButton?.style?.display=if(canBatch&&selecting)"" else "none"
        shownOpenIds=filtered.filter { field(it.data,"status").ifBlank { "open" } == "open" }.map { it.id }
        element("resolve-all-issues")?.style?.display=if(canBatch&&!selecting&&!insights&&shownOpenIds.isNotEmpty())"" else "none"
        batchButton?.textContent=if(selecting)"Resolve selected (${selectedIds.size})"else"Select issues to resolve"
        if(selecting&&selectedIds.isEmpty())batchButton?.setAttribute("disabled","")else batchButton?.removeAttribute("disabled")
        element("issue-stats")?.innerHTML = statCard("Open incidents", rows.count { field(it.data,"status").ifBlank { "open" } == "open" }, "error", "red") + statCard("Resolved", resolvedTotal ?: if (resolvedCountFailed) "Unavailable" else "…", "task_alt", "green") + statCard("Apps (last 200 issues)", shown(recent).map { field(it.data,"appName") }.filter { it.isNotBlank() }.distinct().size, "apps", "blue") + statCard("Companies (last 200 issues)", shown(recent).map { companyIdOf(it.data) }.filter { it.isNotBlank() }.distinct().size, "business", "slate")
        grid.classList.toggle("grid", !insights)
        if (insights) {
            batchButton?.style?.display = "none"; cancelButton?.style?.display = "none"
            loadInsights()
            if (insightRows == null) { grid.innerHTML = Infographic.loading(insightRange, ""); Infographic.bindRange(grid, insightRange) { insightsAnimate = true; draw() }; return }
            val unfiltered = !searching && appFilter.isBlank() && severityFilter == null
            drawInsights(grid, (insightRows.orEmpty() + rows).distinctBy { it.id }.filter { record ->
                val data = record.data
                (companyFilter.isBlank() || companyIdOf(data) == companyFilter) && (appFilter.isBlank() || appOf(data) == appFilter) && (severityFilter == null || severityOf(data) == severityFilter) &&
                    (search.isBlank() || matchesSearch(search, data))
            }, unfiltered)
            return
        }
        if (filtered.isEmpty()) {
            val none = rows.none { inStatus(it.data) }
            grid.innerHTML = when {
                !none -> emptyState(EmptyStates.noMatches("issues"), "search")
                active == "open" -> emptyState(EmptyStates.openIssues, "verified_user")
                else -> emptyState(EmptyStates.resolvedIssues, "task_alt")
            }
            return
        }
        val now = (js("Date.now()") as Double).toLong()
        grid.innerHTML = filtered.joinToString("") { record ->
            val d = record.data; val status = field(d,"status").ifBlank { "open" }; val message = field(d,"errorMessage").ifBlank { field(d,"message") }
            val chosen=selectedIds.contains(record.id)
            val app=field(d,"appName").ifBlank { "Application incident" }
            val where=listOf(IncidentFields.companyName(field(d,"company").ifBlank { field(d,"appVariant") }),field(d,"appVersion").let { if (it.isBlank()) "" else "v$it" }).filter{it.isNotBlank()}.joinToString(" · ")
            val who=listOf(field(d,"deviceName").ifBlank { field(d,"deviceModel") },field(d,"operatorName")).filter{it.isNotBlank()}.joinToString(" · ").ifBlank { "Unknown device" }
            val resolved=status=="resolved"
            val level=severityOf(d)
            val check=if(selecting)"<span class=\"material-symbols-rounded ic-check\" aria-hidden=\"true\">${if(chosen)"check_circle"else"radio_button_unchecked"}</span>" else ""
            """<button type="button" data-sev="$level" class="ic-card ${if(resolved)"is-resolved" else ""} ${if(chosen)"selected" else ""}" data-issue="${escape(record.id)}" aria-pressed="${if(selecting)chosen else false}">""" +
                """<span class="ic-top">$check<span class="ic-app">${avatarHtml(app)}<span><strong>${escape(app)}</strong><small>${escape(where)}</small></span></span>""" +
                """<span class="ic-sev"><i></i>${if (resolved) "Resolved" else IncidentSeverity.label(level)}</span></span>""" +
                """<span class="ic-error"><strong>${escape(field(d,"errorType").ifBlank { field(d,"title").ifBlank { "Technical error" } })}</strong><span>${escape(message.ifBlank { "No message recorded." })}</span></span>""" +
                """<span class="ic-foot"><span class="ic-who"><span class="material-symbols-rounded" aria-hidden="true">smartphone</span>${escape(who)}</span>""" +
                """<span class="ic-when" title="${escape(timeLabel(d))}">${escape(NotificationKinds.relative(occurredMillis(d), now))}</span></span></button>"""
        }
        document.querySelectorAll("[data-issue]").let { nodes -> for (i in 0 until nodes.length) nodes.item(i)?.addEventListener("click", { e ->
            rows.firstOrNull { it.id == e.currentTarget.asDynamic().getAttribute("data-issue") }?.let { record -> if(selecting){if(!selectedIds.add(record.id))selectedIds.remove(record.id);draw()}else openDetail(record) }
        }) }
    }

    private fun cancelBatchSelection(){selecting=false;selectedIds.clear()}

    /** Open issues the filters show right now, for Resolve all. */
    private var shownOpenIds = emptyList<String>()

    /** Resolve all: ticks every open issue shown, then Yes resolves them and No puts everything back. */
    private fun confirmResolveAll(){
        if(shownOpenIds.isEmpty())return
        selecting=true;selectedIds.clear();selectedIds.addAll(shownOpenIds);draw()
        confirmModal("Resolve all ${selectedIds.size} issues?","Are you sure? Every open issue shown will move to the Resolved list.","confirm-resolve-all","Yes",icon="task_alt",tone="success",cancelLabel="No")
        click("confirm-resolve-all"){resolveSelectedIssues()}
        click("confirm-cancel"){cancelBatchSelection();draw()}
    }

    private fun confirmBatchResolve(){
        val count=selectedIds.size;if(count==0)return
        confirmModal("Resolve $count selected issues?","Only the issues you selected will move to the Resolved list.","confirm-batch-resolve","Resolve",icon="task_alt",tone="success")
        click("confirm-batch-resolve"){resolveSelectedIssues()}
    }

    private fun resolveSelectedIssues(){
        val ids=selectedIds.toList();if(ids.isEmpty())return
        val fs=FirebaseGateway.firestore;val user=FirebaseGateway.currentUser()
        // A batch holds at most 500 writes; Resolve all can pass more.
        val commits=ids.chunked(450).map{chunk->val batch=fs.writeBatch(FirebaseGateway.db)
            chunk.forEach{id->batch.update(FirebaseGateway.document("incidents/$id"),json("status" to "resolved","resolvedAt" to FirebaseGateway.serverTimestamp(),"resolvedBy" to(user?.email?:user?.uid?:"")))}
            batch.commit()}
        promise(js("Promise").all(commits.toTypedArray()),{refreshResolvedTotal();cancelBatchSelection();closeModal();toast("${ids.size} issues resolved.");draw()},{message->toast(message,true)})
    }

    private fun openDetail(record: WebRecord) {
        val d = record.data; val status = field(d,"status").ifBlank { "open" }
        val resolved = status == "resolved"
        val app = field(d,"appName").ifBlank { "Application incident" }
        val flat = flatten(d)
        val sections = IncidentFields.sections(flat)
        val mapsUrl = IncidentFields.mapsUrl(flat)

        val level = severityOf(d)
        val device = field(d, "deviceName").ifBlank { field(d, "deviceModel") }
        fun fact(icon: String, label: String, value: String, extra: String = "") =
            "<div class=\"id-fact\" $extra><span class=\"material-symbols-rounded\" aria-hidden=\"true\">$icon</span><div><small>${escape(label)}</small><strong>${value}</strong></div></div>"
        val facts = fact("schedule", "When", timeLabel(d).split('\n').joinToString("<br>") { escape(it) }) +
            fact("smartphone", "Device", escape(device.ifBlank { "Unknown" })) +
            fact("person", "Operator", escape(field(d, "operatorName").ifBlank { "Unknown" })) +
            fact("priority_high", "Severity", escape(IncidentSeverity.title(level)), "data-sev=\"$level\"")

        fun sectionIcon(title: String) = when (title) {
            "Location" -> "location_on"
            "App" -> "apps"
            "Device" -> "smartphone"
            "Status" -> "info"
            "Details" -> "tune"
            else -> "folder"
        }

        fun card(section: IncidentSection, wide: Boolean = false, extra: String = "") =
            "<section class=\"id-section ${if (wide) "is-wide" else ""}\"><h3><span class=\"material-symbols-rounded\" aria-hidden=\"true\">${sectionIcon(section.title)}</span>${escape(section.title)}$extra</h3><dl>" +
                section.fields.joinToString("") { "<div><dt>${escape(it.label)}</dt><dd>${escape(it.value)}</dd></div>" } + "</dl></section>"

        val mapButton = if (mapsUrl == null) "" else "<a class=\"id-map\" href=\"${escape(mapsUrl)}\" target=\"_blank\" rel=\"noopener\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">map</span>Open in Google Maps</a>"
        val location = sections.filter { it.title == "Location" }.joinToString("") { card(it, wide = true, extra = mapButton) }
        val fix = if (field(d,"resolutionSuggestion").isBlank()) "" else
            "<div class=\"id-fix\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">lightbulb</span><div><strong>Diagnosis and recommended action</strong><p>${escape(field(d,"resolutionSuggestion"))}</p></div></div>"
        val others = sections.filter { it.title != "Location" }.joinToString("") { card(it) }

        val stack = field(d, "stackTrace")
        val techCard = if (stack.isBlank()) "" else
            "<details class=\"id-tech\"><summary><span class=\"material-symbols-rounded\" aria-hidden=\"true\">terminal</span>Stack trace<span class=\"material-symbols-rounded id-tech-chev\" aria-hidden=\"true\">expand_more</span></summary><pre>${escape(IncidentFields.redact(stack))}</pre></details>"

        val rawCompany = field(d,"company").ifBlank { field(d,"appVariant") }
        val company = IncidentFields.companyName(rawCompany)
        val server = rawCompany.removePrefix(company).trim().trimStart('•', '-').trim()
        val subtitle = listOf(company, field(d,"appVersion").takeIf { it.isNotBlank() }?.let { "v$it" }.orEmpty()).filter { it.isNotBlank() }.joinToString(" · ").ifBlank { "Default variant" }

        val body = """
          <div class="id-wrap">
            <header class="id-head">
              ${avatarHtml(app)}
              <div><h2>${escape(app)}</h2><p>${escape(subtitle)}${if (server.isBlank()) "" else "<code>${escape(server)}</code>"}</p></div>
              <span class="tk-pill tk-status-${if(resolved)"completed" else "open"}">${if(resolved)"Resolved" else "Open"}</span>
            </header>
            <div data-sev="$level" class="id-error ${if(resolved)"is-resolved" else ""}">
              <span class="id-error-icon material-symbols-rounded" aria-hidden="true">${if(resolved)"check_circle" else "error"}</span>
              <div><strong>${escape(field(d,"errorType").ifBlank { "Technical error" })}</strong><p>${escape(field(d,"errorMessage").ifBlank { field(d,"message") }.ifBlank { "No message recorded." })}</p></div>
            </div>
            <div class="id-facts">$facts</div>
            $fix
            <div class="id-sections">$location$others</div>
            $techCard
          </div>
        """.trimIndent()
        val actions = if (status == "open") "<button id=\"resolve-issue\" class=\"btn btn-success\"><span class=\"material-symbols-rounded\">task_alt</span> Mark as resolved</button>" else ""
        modal("Incident details", body, actions, wide = true, extraClass = "ticket-modal")
        click("resolve-issue") { confirmModal("Resolve incident?", "Confirm this technical issue has been sorted out. It will move to the resolved view.", "confirm-resolve", "Resolve", icon = "task_alt", tone = "success");click("confirm-resolve") { promise(FirebaseGateway.update(record.path, json("status" to "resolved", "resolvedAt" to FirebaseGateway.serverTimestamp(), "resolvedBy" to (FirebaseGateway.currentUser()?.email ?: ""))), { refreshResolvedTotal(); toast("Incident resolved."); closeModal() }) } }
    }

    /** Every field of the record as text: nested maps become dotted keys, list items are numbered from 1, timestamps are dates. */
    internal fun flatten(value: dynamic, key: String = "", out: MutableMap<String, String> = linkedMapOf()): Map<String, String> {
        if (value == null || value == undefined) return out
        val type = jsTypeOf(value)
        when {
            type == "object" && jsTypeOf(value.toDate) == "function" -> out[key] = formatDate(value)
            type == "object" && jsTypeOf(value.isEqual) == "function" && jsTypeOf(value.latitude) == "number" -> out[key] = "${value.latitude}, ${value.longitude}"
            js("Array").isArray(value) as Boolean -> { val list = value.unsafeCast<Array<dynamic>>(); for (i in list.indices) flatten(list[i], "$key.${i + 1}", out) }
            type == "object" -> for (k in js("Object").keys(value).unsafeCast<Array<String>>()) flatten(value[k], if (key.isEmpty()) k else "$key.$k", out)
            else -> out[key] = value.toString() as String
        }
        return out
    }

    /** When it happened on the phone (or arrived, see [IncidentFields.sortMillis]): what the lists sort by. */
    private fun occurredMillis(d: dynamic): Long {
        val details: dynamic = d.details
        val raw: dynamic = if (details == null) null else details.occurredAt
        val occurred = if (jsTypeOf(raw) == "number") (raw as Double) else timestampMillis(raw).toDouble()
        return IncidentFields.sortMillis(occurred.toLong(), timestampMillis(d.timestamp).toLong())
    }

    /** "Occurred …" and "Received …" lines; see IncidentFields.cardTime. occurredAt may be a timestamp or epoch millis. */
    private fun timeLabel(d: dynamic): String {
        val details: dynamic = d.details
        val raw: dynamic = if (details == null) null else details.occurredAt
        val occurredMillis = if (jsTypeOf(raw) == "number") (raw as Double) else timestampMillis(raw).toDouble()
        return IncidentFields.cardTime(if (occurredMillis > 0) formatDate(kotlin.js.Date(occurredMillis)) else null, if (d.timestamp == null) null else formatDate(d.timestamp))
    }

}
